import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import styled, { css } from "styled-components";

import { MessageContent as MessageContentRenderer, MessageInput, isMessageDocEmpty } from "components/atoms/message";

import {
    formatReviewThreadCreatedAt,
    formatReviewThreadLocation,
    getReviewThreadSelectedText,
    getReviewThreadStatusLabel,
} from "components/organisms/manuscript/review/formatters";

const Shell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
`;

const OverlayShell = styled.div`
    position: fixed;
    z-index: 60;
    display: flex;
    flex-direction: column;
    width: min(400px, calc(100vw - 24px));
    max-height: min(440px, calc(100vh - 24px));
    padding: 10px;
    border: 1px solid #cfe0d7;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 18px 40px rgba(15, 76, 129, 0.18);
    backdrop-filter: blur(10px);
    overflow: hidden;
`;

const BodyScroll = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
    max-height: ${(props) => (props.$compact ? "380px" : "420px")};
    overflow: auto;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const TitleGroup = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-size: 13px;
    font-weight: 800;
    color: #132238;
`;

const Subtitle = styled.div`
    margin-top: 2px;
    font-size: 11px;
    color: #6d7f96;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 20px;
    padding: 0 8px;
    border-radius: 999px;
    background: ${(props) => (props.$resolved ? "#e5ecf4" : "#e7faf3")};
    color: ${(props) => (props.$resolved ? "#5f7489" : "#0f8f6f")};
    font-size: 10px;
    font-weight: 800;
`;

const HeaderActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    padding: 0 9px;
    border: 1px solid ${(props) => (props.$primary ? "#bfeadd" : "#d7e0ea")};
    border-radius: 999px;
    background: ${(props) => (props.$primary ? "#e7faf3" : "#ffffff")};
    color: ${(props) => (props.$primary ? "#0f8f6f" : "#496178")};
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: ${(props) => (props.$primary ? "#d9f3eb" : "#eef3f7")};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const CloseButton = styled.button`
    width: 28px;
    height: 28px;
    border: 1px solid #d7e0ea;
    border-radius: 8px;
    background: #ffffff;
    color: #496178;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        background: #eef3f7;
    }
`;

const Meta = styled.div`
    display: grid;
    grid-template-columns: minmax(64px, 92px) minmax(0, 1fr);
    gap: 8px 12px;
    font-size: 12px;
`;

const MetaKey = styled.div`
    font-weight: 700;
    color: #29415d;
`;

const MetaValue = styled.div`
    min-width: 0;
    color: #536981;
    word-break: break-word;
`;

const MessageList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MessageCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid ${(props) => (props.$focused ? "#0f8f6f" : "#dfe7f0")};
    border-radius: 10px;
    background: ${(props) => (props.$focused ? "#eef9f4" : "#ffffff")};
`;

const MessageMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 11px;
    color: #72849a;
`;

const MessageContent = styled.div`
    font-size: 13px;
    line-height: 1.5;
    color: #23374d;
    white-space: pre-wrap;
    word-break: break-word;
`;

const Composer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AiRequestLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #40576f;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    input {
        width: 14px;
        height: 14px;
        accent-color: #0f8f6f;
    }
`;

const panelCardStyle = css`
    padding: 12px;
    border: 1px solid #dde7f1;
    border-radius: 12px;
    background: #ffffff;
`;

const overlayCardStyle = css`
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
`;

const Container = styled.div`
    ${(props) => (props.$variant === "overlay" ? overlayCardStyle : panelCardStyle)}
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
`;

export default function ManuscriptReviewThreadOverlay({
    focusedMessageId,
    thread = null,
    composerAnchor = null,
    position = null,
    overlayRef,
    replyDraft,
    replyRequestAI = false,
    composerDraft,
    onReplyDraftChange,
    onReplyRequestAIChange,
    onComposerDraftChange,
    onCreateReview,
    onReplyReview,
    onResolveReview,
    onReopenReview,
    onClose,
    onBeforeReply,
    onBeforeReopen,
    onReplySuccess,
    requestAIReview = false,
    onRequestAIReviewChange,
    mentions,
    replyLoading = false,
    createLoading = false,
    resolveLoading = false,
    reopenLoading = false,
}) {
    if ((!thread && !composerAnchor) || !position) {
        return null;
    }

    return createPortal(
        <OverlayShell
            ref={overlayRef}
            data-testid={thread ? "review-overlay" : "review-composer-overlay"}
            style={{
                top: position.top,
                left: position.left,
            }}
        >
            <ReviewThreadOverlayContent
                thread={thread}
                focusedMessageId={focusedMessageId}
                composerAnchor={composerAnchor}
                variant="overlay"
                replyDraft={replyDraft}
                replyRequestAI={replyRequestAI}
                composerDraft={composerDraft}
                onReplyDraftChange={onReplyDraftChange}
                onReplyRequestAIChange={onReplyRequestAIChange}
                onComposerDraftChange={onComposerDraftChange}
                onCreate={async () => {
                    await onCreateReview?.(composerDraft, composerAnchor, { requestAI: requestAIReview });
                }}
                onReply={async () => {
                    onBeforeReply?.();
                    const created = await onReplyReview?.(thread.id, replyDraft, { requestAI: replyRequestAI });
                    if (created !== false) {
                        onReplySuccess?.();
                    }
                }}
                onResolve={async () => {
                    const resolved = await onResolveReview?.(thread.id);
                    if (resolved !== false) {
                        onClose?.();
                    }
                }}
                onReopen={async () => {
                    onBeforeReopen?.();
                    await onReopenReview?.(thread.id);
                }}
                onClose={onClose}
                requestAIReview={requestAIReview}
                onRequestAIReviewChange={onRequestAIReviewChange}
                mentions={mentions}
                replyLoading={replyLoading}
                createLoading={createLoading}
                resolveLoading={resolveLoading}
                reopenLoading={reopenLoading}
            />
        </OverlayShell>,
        document.body,
    );
}

function ReviewThreadOverlayContent({
    thread = null,
    focusedMessageId,
    composerAnchor = null,
    replyDraft,
    replyRequestAI = false,
    composerDraft,
    onReplyDraftChange,
    onReplyRequestAIChange,
    onComposerDraftChange,
    onReply,
    onCreate,
    onResolve,
    onReopen,
    onClose,
    requestAIReview = false,
    onRequestAIReviewChange,
    mentions,
    replyLoading = false,
    createLoading = false,
    resolveLoading = false,
    reopenLoading = false,
    variant = "panel",
}) {
    const bodyScrollRef = useRef(null);
    const messageRefs = useRef(new Map());
    const [highlightedMessageId, setHighlightedMessageId] = useState(focusedMessageId);
    const isComposer = !thread && !!composerAnchor?.creation_anchor_range;
    const messages = thread?.messages || [];
    const lastMessage = messages[messages.length - 1] || null;
    const metaSource = isComposer ? composerAnchor?.creation_anchor_range : thread?.creation_anchor_range;
    const location = formatReviewThreadLocation(metaSource);
    const selectedText = getReviewThreadSelectedText(metaSource);
    const title = isComposer ? "새 리뷰" : thread?.anchor_path;
    const subtitle = isComposer ? "선택 영역에 코멘트를 남깁니다." : "리뷰";

    useEffect(() => {
        if (!focusedMessageId || !thread) return;
        setHighlightedMessageId(focusedMessageId);
        const frame = requestAnimationFrame(() => {
            messageRefs.current.get(String(focusedMessageId))?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        });
        const timer = setTimeout(() => setHighlightedMessageId(null), 3000);
        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(timer);
        };
    }, [focusedMessageId, thread]);

    useEffect(() => {
        if (isComposer || focusedMessageId) return;
        const frame = requestAnimationFrame(() => {
            const scrollEl = bodyScrollRef.current;
            if (!scrollEl) return;
            scrollEl.scrollTo({
                top: scrollEl.scrollHeight,
                behavior: "smooth",
            });
        });
        return () => cancelAnimationFrame(frame);
    }, [focusedMessageId, isComposer, lastMessage?.content, lastMessage?.id, messages.length]);

    if (!thread && !isComposer) {
        return null;
    }

    return (
        <Container $variant={variant}>
            <Shell>
                <Header>
                    <TitleGroup>
                        <Title>{title}</Title>
                        <Subtitle>{subtitle}</Subtitle>
                    </TitleGroup>
                    <HeaderActions>
                        {!isComposer ? (
                            <Badge $resolved={thread.is_resolved}>{getReviewThreadStatusLabel(thread)}</Badge>
                        ) : null}
                        {!isComposer ? (
                            thread.is_resolved ? (
                                <HeaderActionButton type="button" onClick={onReopen} disabled={reopenLoading}>
                                    다시 열기
                                </HeaderActionButton>
                            ) : (
                                <HeaderActionButton
                                    type="button"
                                    $primary
                                    onClick={onResolve}
                                    disabled={resolveLoading}
                                >
                                    해결
                                </HeaderActionButton>
                            )
                        ) : null}
                        {onClose ? (
                            <CloseButton
                                type="button"
                                aria-label={isComposer ? "리뷰 입력 닫기" : "리뷰 상세 닫기"}
                                onClick={onClose}
                            >
                                ×
                            </CloseButton>
                        ) : null}
                    </HeaderActions>
                </Header>

                {location ? (
                    <Meta>
                        <MetaKey>위치</MetaKey>
                        <MetaValue>{location}</MetaValue>
                        {selectedText ? (
                            <>
                                <MetaKey>선택 텍스트</MetaKey>
                                <MetaValue>{selectedText}</MetaValue>
                            </>
                        ) : null}
                    </Meta>
                ) : null}

                <BodyScroll ref={bodyScrollRef} $compact={variant === "panel"}>
                    {!isComposer ? (
                        <MessageList>
                            {messages.map((message) => (
                                <MessageCard
                                    key={message.id}
                                    ref={(node) => {
                                        if (node) messageRefs.current.set(String(message.id), node);
                                        else messageRefs.current.delete(String(message.id));
                                    }}
                                    data-review-message-id={message.id}
                                    data-focused={String(message.id) === String(highlightedMessageId)}
                                    $focused={String(message.id) === String(highlightedMessageId)}
                                >
                                    <MessageMeta>
                                        <div>{message.sender_name || message.sender_email || "Unknown"}</div>
                                        <div>{formatReviewThreadCreatedAt(message.created_at)}</div>
                                    </MessageMeta>
                                    <MessageContent>
                                        <MessageContentRenderer content={message.content} />
                                    </MessageContent>
                                </MessageCard>
                            ))}
                        </MessageList>
                    ) : null}

                    <Composer>
                        <MessageInput
                            value={isComposer ? composerDraft : replyDraft}
                            onChange={(nextValue) =>
                                isComposer ? onComposerDraftChange?.(nextValue) : onReplyDraftChange?.(nextValue)
                            }
                            onSubmit={isComposer ? onCreate : onReply}
                            placeholder={isComposer ? "리뷰 코멘트를 입력하세요." : "답글을 입력하세요."}
                            mentions={mentions}
                            submitTooltip={isComposer ? "리뷰 남기기" : "답글"}
                            submitDisabled={
                                isComposer
                                    ? isMessageDocEmpty(composerDraft) ||
                                      createLoading ||
                                      !composerAnchor?.creation_anchor_range
                                    : isMessageDocEmpty(replyDraft) || replyLoading || thread.is_resolved
                            }
                            submitLoading={isComposer ? createLoading : replyLoading}
                            bottomSlot={
                                isComposer ? (
                                    <AiRequestLabel>
                                        <input
                                            type="checkbox"
                                            checked={requestAIReview}
                                            onChange={(event) => onRequestAIReviewChange?.(event.target.checked)}
                                            disabled={createLoading}
                                        />
                                        AI에게 요청
                                    </AiRequestLabel>
                                ) : (
                                    <AiRequestLabel>
                                        <input
                                            type="checkbox"
                                            checked={replyRequestAI}
                                            onChange={(event) => onReplyRequestAIChange?.(event.target.checked)}
                                            disabled={replyLoading}
                                        />
                                        AI 자동 응답
                                    </AiRequestLabel>
                                )
                            }
                        />
                    </Composer>
                </BodyScroll>
            </Shell>
        </Container>
    );
}
